import {useQuery, useQueryClient} from '@tanstack/react-query';
import type {JiraObject} from 'client';
import {WorkstreamService} from 'client';
import {useCallback, useMemo} from 'react';
import {getService} from 'utilities';

import {
  getMockElevatableJiraObjects,
  getMockWorkstreamJiraObjects,
  type MockJiraParentWorkstream,
  shouldUseMockJiraObjects,
} from './mockJiraObjects';

const ROADMAP_INITIATIVES_STALE_TIME_MS = 60 * 1000;

export const getWorkstreamRoadmapInitiativesQueryKey = (workstreamId: number) =>
  ['workstream', 'jira', workstreamId] as const;

export const getMockJiraElevationsQueryKey = (workstreamId: number) =>
  ['workstream', 'jira', 'mock-elevations', workstreamId] as const;

// `refresh` also has the server refresh them from Jira in the background (an
// open card or a detail page, not a table row); a broadcast refetches them if
// they changed. Same cache either way.
export const fetchWorkstreamJiraObjects = (
  workstreamId: number,
  refresh = false
): Promise<JiraObject[]> =>
  getService(WorkstreamService).getJiraObjectsApiV1WorkstreamIdJiraGet(workstreamId, refresh);

export const useWorkstreamRoadmapInitiatives = (
  workstreamId: number,
  enabled: boolean,
  refresh = false
) =>
  useQuery({
    // `refresh` changes only the server's side effect, not the data, so it stays out of the key.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: getWorkstreamRoadmapInitiativesQueryKey(workstreamId),
    queryFn: () => fetchWorkstreamJiraObjects(workstreamId, refresh),
    enabled: enabled && workstreamId > 0,
    staleTime: ROADMAP_INITIATIVES_STALE_TIME_MS,
  });

export const useWorkstreamJiraElevations = (
  workstream: MockJiraParentWorkstream,
  enabled: boolean,
  {refresh = false}: {refresh?: boolean} = {}
) => {
  const queryClient = useQueryClient();
  const jiraQuery = useWorkstreamRoadmapInitiatives(workstream.id, enabled, refresh);
  const {refetch: refetchJiraObjects} = jiraQuery;
  const jiraObjects = useMemo(() => jiraQuery.data ?? [], [jiraQuery.data]);
  const useMockJiraObjects = shouldUseMockJiraObjects(jiraObjects, jiraQuery.isError);
  const mockElevationsQueryKey = getMockJiraElevationsQueryKey(workstream.id);
  const {data: mockElevations = []} = useQuery({
    queryKey: mockElevationsQueryKey,
    queryFn: async (): Promise<JiraObject[]> => [],
    initialData: [] as JiraObject[],
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: enabled && useMockJiraObjects,
  });
  const loadMockWorkstream = useCallback(
    (workstreamId: number) =>
      queryClient.fetchQuery({
        queryKey: ['workstream-data', 'workstream-detail', workstreamId],
        queryFn: () => getService(WorkstreamService).getApiV1WorkstreamIdGet(workstreamId),
        staleTime: ROADMAP_INITIATIVES_STALE_TIME_MS,
      }),
    [queryClient]
  );
  const loadMockCandidates = useCallback(
    () => getMockElevatableJiraObjects(workstream, loadMockWorkstream),
    [loadMockWorkstream, workstream]
  );
  const resolvedJiraObjects = useMemo(() => {
    if (!useMockJiraObjects) {
      return jiraObjects;
    }

    const canonicalMocks = getMockWorkstreamJiraObjects(workstream);
    const canonicalIds = new Set(canonicalMocks.map((item) => item.id));
    return [...canonicalMocks, ...mockElevations.filter((item) => !canonicalIds.has(item.id))];
  }, [jiraObjects, mockElevations, useMockJiraObjects, workstream]);

  const loadElevatableJiraOutcomes = useCallback(() => {
    if (!useMockJiraObjects) {
      return getService(
        WorkstreamService
      ).getElevatableJiraObjectsApiV1WorkstreamIdJiraElevatableGet(workstream.id);
    }

    const elevatedIds = new Set(mockElevations.map((item) => item.id));
    return loadMockCandidates().then((mockCandidates) =>
      mockCandidates.filter((item) => !elevatedIds.has(item.id))
    );
  }, [loadMockCandidates, mockElevations, useMockJiraObjects, workstream.id]);

  const elevateJiraOutcome = useCallback(
    async (jiraObjectId: number) => {
      if (!useMockJiraObjects) {
        await getService(WorkstreamService).elevateJiraObjectApiV1WorkstreamIdJiraElevatePost(
          workstream.id,
          {jira_object_id: jiraObjectId}
        );
        await refetchJiraObjects();
        return;
      }

      const mockCandidates = await loadMockCandidates();
      const candidate = mockCandidates.find((item) => item.id === jiraObjectId);
      if (!candidate) {
        throw new Error('Mock Jira initiative cannot be elevated to this workstream');
      }
      queryClient.setQueryData<JiraObject[]>(mockElevationsQueryKey, (current = []) =>
        current.some((item) => item.id === candidate.id) ? current : [...current, candidate]
      );
    },
    [
      loadMockCandidates,
      mockElevationsQueryKey,
      queryClient,
      refetchJiraObjects,
      useMockJiraObjects,
      workstream.id,
    ]
  );

  const detachJiraOutcome = useCallback(
    async (jiraObjectId: number) => {
      if (!useMockJiraObjects) {
        await getService(
          WorkstreamService
        ).detachJiraObjectElevationApiV1WorkstreamIdJiraJiraObjectIdElevationDelete(
          workstream.id,
          jiraObjectId
        );
        await refetchJiraObjects();
        return;
      }

      queryClient.setQueryData<JiraObject[]>(mockElevationsQueryKey, (current = []) =>
        current.filter((item) => item.id !== jiraObjectId)
      );
    },
    [mockElevationsQueryKey, queryClient, refetchJiraObjects, useMockJiraObjects, workstream.id]
  );

  return {
    ...jiraQuery,
    jiraObjects: resolvedJiraObjects,
    loadElevatableJiraOutcomes,
    elevateJiraOutcome,
    detachJiraOutcome,
    isUsingMockJiraObjects: useMockJiraObjects,
  };
};
